import axios from "axios";
import config from "../utils/config";
import { getStoredAttribution } from "../utils/attribution";
import authService from "./auth";
import { API_PATH, joinApiEndpoint } from "../constants/apiEndpoints";
import { PATIENT_AUTH_STORAGE_KEY } from "../constants/patientAuthStorage";
import { parseRateLimitError } from "../utils/rateLimit";

/**
 * Extract user-facing message from API errors.
 * Only returns safe, sanitized error messages to prevent information disclosure.
 * Skips Django REST Framework's verbose .detail field which exposes schema/validation info.
 */
export function getApiErrorMessage(error) {
  const rateLimitMsg = parseRateLimitError(error);
  if (rateLimitMsg) {
    return rateLimitMsg;
  }

  const d = error?.response?.data;

  if (Array.isArray(d?.non_field_errors) && d.non_field_errors.length > 0) {
    return String(d.non_field_errors[0]);
  }

  if (Array.isArray(d?.detail) && d.detail.length > 0) {
    return String(d.detail[0]);
  }

  for (const key of ['email', 'password', 'portal']) {
    if (Array.isArray(d?.[key]) && d[key].length > 0) {
      return String(d[key][0]);
    }
  }

  if (Array.isArray(d?.missing_fields) && d.missing_fields.length > 0) {
    return `${d.error || "Required questions are missing"}: ${d.missing_fields.join("; ")}`;
  }
      
  // Prefer explicit error message set by backend
  if (d?.error && typeof d.error === 'string') {
    return d.error;
  }
  
  // Fallback to generic message field
  if (d?.message && typeof d.message === 'string') {
    return d.message;
  }
  
  // Do NOT use .detail field - it contains verbose validation errors that expose:
  // - Field names and types
  // - Enum values
  // - Business logic constraints
  // - User enumeration hints
  // Example: {"detail": "User with email already exists"} leaks user enumeration
  
  // Fallback to request error message
  if (error?.message && typeof error.message === 'string') {
    return error.message;
  }
  
  // Final fallback
  return "Something went wrong.";
}

class WellieMDAPI {
  constructor() {
    this.api = axios.create({
      baseURL: config.API_BASE_URL,
      timeout: 60000, // Increased from 30000 to 60000ms for payment processing
      headers: { "Content-Type": "application/json" },
    });

    this.sessionToken = null;
    this.responseStartTime = null;

    this.api.interceptors.request.use(
      (config) => {
        const sessionToken = this.getSessionToken();
        if (sessionToken) {
          config.headers["Session-Token"] = sessionToken;
        }

        const clientId = this.getClientId();
        if (clientId) {
          config.headers["X-Client-ID"] = clientId;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          authService.logout();
          window.dispatchEvent(new CustomEvent("welliemd:auth-error"));
        }
        return Promise.reject(error);
      }
    );
  }

  setSessionToken(token) {
    this.sessionToken = token;
  }

  getSessionToken() {
    return this.sessionToken;
  }

  clearSessionToken() {
    this.sessionToken = null;
  }

  getClientId() {
    const clientId = localStorage.getItem("welliemd_client_id");
    return clientId; // Don't throw error, return null if not found
  }

  // 🚀 UPDATED: Frontend-driven questionnaire endpoints using Bearer token (CLIENT_WEBHOOK_TOKEN)
  async initializeFrontendQuestionnaire(templateId = null, patientId = null, prescribedDose = null) {
    // Get API token from environment (CLIENT_WEBHOOK_TOKEN)
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    if (!apiToken) {
      throw new Error("API token not configured. Please contact support.");
    }

    // Use client-specific API endpoint for questionnaire operations
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );

    if (!clientApiEndpoint) {
      throw new Error(
        "Client API endpoint not found. Please refresh the page."
      );
    }

    const requestBody = {};

    if (templateId) {
      requestBody.template_id = templateId;
    }
    
    // NEW: Include patient context for dose filtering
    if (patientId) {
      requestBody.patient_id = patientId;
    }
    if (prescribedDose) {
      requestBody.prescribed_dose = prescribedDose;
    }

    console.log("🚀 Initializing frontend questionnaire with Bearer token");
    console.log("📍 Using client API:", clientApiEndpoint);

    // Get client ID for headers
    const clientId = this.getClientId();
    const followUpAccessToken = sessionStorage.getItem("followup_access_token");

    // Use client API endpoint instead of admin API
    const apiUrl = joinApiEndpoint(clientApiEndpoint, API_PATH.questionnaireInitialize);

    const response = await axios.post(apiUrl, requestBody, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
        "Content-Type": "application/json",
        ...(clientId && { "X-Client-ID": clientId }),
        ...(followUpAccessToken && {
          "X-FollowUp-Access-Token": followUpAccessToken,
        }),
      },
      timeout: 60000, // Increased timeout for initialization
    });

    if (response.data.success) {
      // Store session token for completion
      this.sessionToken = response.data.session_token;
      console.log("✅ Frontend questionnaire initialized successfully");
      return response.data;
    }

    throw new Error(
      response.data.error || "Failed to initialize frontend questionnaire"
    );
  }

  async completeFrontendQuestionnaire(
    responses,
    patientInfo,
    completionTimeMs = 0,
    navigationHistory = [],
    paymentData = null
  ) {
    if (!this.sessionToken) {
      throw new Error("No active session token");
    }

    // Get API token from environment (CLIENT_WEBHOOK_TOKEN)
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    if (!apiToken) {
      throw new Error("API token not configured");
    }

    // Use client-specific API endpoint
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );

    if (!clientApiEndpoint) {
      throw new Error("Client API endpoint not found");
    }

    const payload = {
      session_token: this.sessionToken,
      responses: responses,
      patient_info: patientInfo,
      completion_time_ms: completionTimeMs,
      navigation_history: navigationHistory,
    };

    // Handle payment data - can be in different formats
    if (paymentData) {
      console.log("🔍 Processing paymentData:", paymentData);
      if (paymentData.gateway) {
        payload.gateway = paymentData.gateway;
      }

      // Extract coupon and referral info if present
      if (paymentData.coupon_code)
        payload.coupon_code = paymentData.coupon_code;
      if (paymentData.referral_campaign)
        payload.referral_campaign = paymentData.referral_campaign;
      if (paymentData.referral_source)
        payload.referral_source = paymentData.referral_source;

      // If paymentData has payment_data property, use it
      if (paymentData.payment_data) {
        payload.payment_data = paymentData.payment_data;
        console.log("✅ Using paymentData.payment_data:", payload.payment_data);
      }
      // Otherwise, assume payment data properties are directly on paymentData
      else {
        const {
          gateway,
          payment_data,
          coupon_code,
          referral_campaign,
          referral_source,
          ...paymentFields
        } = paymentData;
        if (Object.keys(paymentFields).length > 0) {
          payload.payment_data = paymentFields;
          console.log("✅ Extracted payment fields:", payload.payment_data);
        } else {
          console.log("❌ No payment fields found in paymentData");
        }
      }
    } else {
      console.log("❌ No paymentData provided");
    }

    console.log("📤 Final payload:", payload);

    console.log("🚀 Completing frontend questionnaire with Bearer token");
    console.log("📍 Using client API:", clientApiEndpoint);

    // Get client ID for headers
    const clientId = this.getClientId();

    // Check for follow-up context and get access token if available
    const followUpAccessToken = sessionStorage.getItem("followup_access_token");
    if (followUpAccessToken) {
      console.log(
        "🔓 Follow-up context detected, including X-FollowUp-Access-Token header"
      );
    }

    // Check for existing patient session (from questionnaire login flow)
    const existingPatientSessionId = sessionStorage.getItem(
      "existing_patient_session_id"
    );
    if (existingPatientSessionId) {
      console.log(
        "👤 Existing patient session detected, including session_id:",
        existingPatientSessionId
      );
      // Add to payload so backend routes to existing patient flow
      payload.followup_session_id = existingPatientSessionId;
    }

    // Also check for existing patient access token
    const existingPatientAccessToken = sessionStorage.getItem(
      "existing_patient_access_token"
    );

    const apiUrl = joinApiEndpoint(clientApiEndpoint, API_PATH.questionnaireComplete);

    const response = await axios.post(apiUrl, payload, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
        "Content-Type": "application/json",
        ...(clientId && { "X-Client-ID": clientId }),
        ...(followUpAccessToken && {
          "X-FollowUp-Access-Token": followUpAccessToken,
        }),
        ...(existingPatientAccessToken && {
          "X-ExistingPatient-Access-Token": existingPatientAccessToken,
        }),
      },
      timeout: 60000, // Increased timeout for payment processing
    });

    if (response.data.success) {
      console.log("✅ Questionnaire completed successfully");
      return response.data;
    }

    throw new Error(response.data.error || "Failed to complete questionnaire");
  }

  async getQuestionnaireStatus(sessionToken) {
    // Get API token from environment (CLIENT_WEBHOOK_TOKEN)
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    if (!apiToken) {
      throw new Error("API token not configured");
    }

    // Use client-specific API endpoint
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );

    if (!clientApiEndpoint) {
      throw new Error("Client API endpoint not found");
    }

    // Get client ID for headers
    const clientId = this.getClientId();

    const apiUrl = joinApiEndpoint(clientApiEndpoint, API_PATH.questionnaireStatus(sessionToken));

    const response = await axios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
        "Content-Type": "application/json",
        ...(clientId && { "X-Client-ID": clientId }),
      },
      timeout: 45000, // Increased timeout for status checks
    });

    if (response.data.success) {
      return response.data;
    }

    throw new Error(
      response.data.error || "Failed to get questionnaire status"
    );
  }

  async saveQuestionnaireDraft(
    sessionToken,
    draftResponses,
    currentQuestionIndex,
    templateVersion
  ) {
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );

    if (!clientApiEndpoint) throw new Error("Client API endpoint not found");

    const apiUrl = joinApiEndpoint(clientApiEndpoint, API_PATH.questionnaireSaveDraft);
    const clientId = this.getClientId();

    const response = await axios.post(
      apiUrl,
      {
        session_token: sessionToken,
        draft_responses: draftResponses,
        current_question_index: currentQuestionIndex,
        template_version: templateVersion,
      },
      {
        headers: {
          Authorization: `Bearer ${apiToken}`,
          "Content-Type": "application/json",
          ...(clientId && { "X-Client-ID": clientId }),
        },
      }
    );

    return response.data;
  }

  async getQuestionnaireDraft(sessionToken) {
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );

    if (!clientApiEndpoint) throw new Error("Client API endpoint not found");

    const apiUrl = joinApiEndpoint(clientApiEndpoint, API_PATH.questionnaireGetDraft);
    const clientId = this.getClientId();

    const response = await axios.get(apiUrl, {
      params: { session_token: sessionToken },
      headers: {
        Authorization: `Bearer ${apiToken}`,
        "Content-Type": "application/json",
        ...(clientId && { "X-Client-ID": clientId }),
      },
    });

    return response.data;
  }

  // Payment configuration endpoint
  async getPaymentConfig() {
    // Get optional API token from environment (CLIENT_WEBHOOK_TOKEN)
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    // Use client-specific API endpoint
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );

    if (!clientApiEndpoint) {
      throw new Error("Client API endpoint not found");
    }

    // Get client ID for headers
    const clientId = this.getClientId();

    const apiUrl = joinApiEndpoint(clientApiEndpoint, API_PATH.questionnairePaymentConfig);

    const headers = {
      "Content-Type": "application/json",
      ...(clientId && { "X-Client-ID": clientId }),
    };
    if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;

    if (
      typeof window !== "undefined" &&
      window.console &&
      window.console.debug
    ) {
      console.debug("[WellieMDAPI] getPaymentConfig ->", apiUrl, { headers });
    }

    const response = await axios.get(apiUrl, { headers, timeout: 45000 });

    // Support both response shapes: { success: true, data: {...} } or direct config object
    if (response.data) {
      if (response.data.success && response.data.data)
        return response.data.data;
      if (
        response.data.active_gateway ||
        response.data.stripe_publishable_key ||
        response.data.nmi_client_key ||
        response.data.authorize_net_client_key
      ) {
        return response.data;
      }
    }

    throw new Error("Failed to get payment config");
  }

  // Get available states for state_routing question type
  async getAvailableStates() {
    // Use client-specific API endpoint
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );

    if (!clientApiEndpoint) {
      throw new Error(
        "Client API endpoint not found. Please refresh the page."
      );
    }

    const apiUrl = joinApiEndpoint(clientApiEndpoint, API_PATH.questionnaireAvailableStates);

    console.log("📍 Fetching available states from:", apiUrl);

    const response = await axios.get(apiUrl, {
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 30000,
    });

    if (response.data) {
      console.log("✅ Available states fetched successfully");
      return response.data;
    }

    throw new Error("Failed to fetch available states");
  }

  // Coupon validation (for UX preview - backend validates again at checkout)
  async validateCoupon(code, subtotal = 0, productId = null, checkoutToken = null) {
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    if (!clientApiEndpoint) {
      throw new Error("Client API endpoint not found");
    }

    const payload = {
      code,
      subtotal: parseFloat(subtotal) || 0,
    };

    if (productId) {
      payload.product_id = productId;
    }
    if (checkoutToken) {
      payload.checkout_token = checkoutToken;
    }

    const headers = {
      "Content-Type": "application/json",
    };
    if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;

    const response = await axios.post(
      joinApiEndpoint(clientApiEndpoint, API_PATH.couponValidate),
      payload,
      { headers, timeout: 10000 }
    );

    return response.data;
  }

  /**
   * Resolve product IDs to current product data.
   *
   * This fetches live product data (price, name, description, shipping) from the backend
   * instead of using stale cached data stored in templates. This ensures product updates
   * propagate automatically without requiring template reassignment.
   *
   * @param {number[]} productIds - Array of product IDs to resolve
   * @returns {Promise<Object[]>} Array of product data with current pricing
   */
  async resolveProducts(productIds) {
    if (!productIds || productIds.length === 0) {
      return [];
    }

    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    if (!clientApiEndpoint) {
      console.error("Client API endpoint not found for product resolution");
      return [];
    }

    try {
      const headers = {
        "Content-Type": "application/json",
      };
      if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;
      const clientId = this.getClientId();
      if (clientId) headers["X-Client-ID"] = clientId;

      const response = await axios.post(
        joinApiEndpoint(clientApiEndpoint, API_PATH.questionnaireProductResolve),
        { product_ids: productIds },
        { headers, timeout: 15000 }
      );

      if (response.data && response.data.products) {
        console.log(
          `Resolved ${response.data.products.length} products with live data`
        );
        return response.data.products;
      }

      return [];
    } catch (error) {
      console.error("Failed to resolve products:", error);
      // Return empty array on error - UI will handle gracefully
      return [];
    }
  }

  // Keep existing backend-driven methods unchanged...
  async initializeSession(templateId, patientId = null) {
    if (!authService.isAuthenticated()) {
      throw new Error("No valid authentication token");
    }

    const requestBody = { template_id: templateId };
    if (patientId) {
      requestBody.patient_id = patientId;
    }

    const response = await this.api.post(
      "/questionnaires/sessions/initialize/",
      requestBody
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || "Failed to initialize session");
  }

  // 🚀 NEW: Checkout Flow API Methods

  /**
   * Patient onboarding - creates or retrieves patient record
   * @param {string} email - Patient email address
   * @param {string} phone - Patient phone number (optional)
   * @param {string} firstName - Patient first name (optional)
   * @param {string} lastName - Patient last name (optional)
   * @returns {Promise<Object>} - {patient_id, is_new_patient}
   */

  async onboardPatient(
    email,
    phone = null,
    {
      first_name,
      last_name,
      template_id,
      password,
      date_of_birth,
      sex,
      email_verification_token,
      require_email_verification,
      issue_patient_access_token,
    } = {}
  ) {

    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    if (!clientApiEndpoint) {
      throw new Error(
        "Client API endpoint not found. Please refresh the page."
      );
    }

    const payload = { email };
    if (phone) {
      payload.phone = phone;
    }
    if (first_name) payload.first_name = first_name;
    if (last_name) payload.last_name = last_name;
    if (template_id) payload.template_id = template_id;
    if (password) payload.password = password;
    if (date_of_birth) payload.date_of_birth = date_of_birth;
    if (sex) payload.sex = sex;
    if (email_verification_token) {
      payload.email_verification_token = email_verification_token;
    }
    if (require_email_verification) {
      payload.require_email_verification = true;
    }
    if (issue_patient_access_token) {
      payload.issue_patient_access_token = true;
    }

    const trimmedFirstName =
      typeof first_name === "string" ? first_name.trim() : first_name;
    const trimmedLastName =
      typeof last_name === "string" ? last_name.trim() : last_name;

    if (trimmedFirstName) {
      payload.first_name = trimmedFirstName;
    }
    if (trimmedLastName) {
      payload.last_name = trimmedLastName;
    }

    const headers = {
      "Content-Type": "application/json",
    };
    if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;

    const clientId = this.getClientId();
    if (clientId) headers["X-Client-ID"] = clientId;

    console.log("🚀 Onboarding patient:", email);

    try {
      const response = await axios.post(
        joinApiEndpoint(clientApiEndpoint, API_PATH.patientOnboarding),
        payload,
        { headers, timeout: 60000 }
      );

      if (
        response.data &&
        (response.data.patient_id || response.data.success)
      ) {
        console.log("✅ Patient onboarded successfully");
        if (response.data.access_token) {
          sessionStorage.setItem(
            PATIENT_AUTH_STORAGE_KEY.runtimeAccessToken,
            response.data.access_token,
          );
          sessionStorage.setItem(
            PATIENT_AUTH_STORAGE_KEY.runtimeAccessExpiresAt,
            String(Date.now() + Number(response.data.access_token_expires_in || 3600) * 1000)
          );
        }
        return response.data;
      }

      throw new Error(response.data.error || "Failed to onboard patient");
    } catch (error) {
      console.error("❌ Patient onboarding failed:", error);
      const normalizedError = new Error(
        getApiErrorMessage(error) || "Failed to onboard patient. Please try again.",
      );
      normalizedError.code =
        error?.response?.data?.code || error?.response?.data?.error_code || null;
      normalizedError.status = error?.response?.status || null;
      throw normalizedError;
    }
  }

  /**
   * Create order with questionnaire data and selected medicines
   * @param {string} patientId - Patient ID from onboarding
   * @param {Object} questionnaireAnswers - Questionnaire responses
   * @param {Array} selectedMedicines - Selected medicines with quantities
   * @param {string} orderType - Order type (onboarding, follow_up)
   * @param {string} [questionnaireSessionId] - Questionnaire session ID so backend can use template.beluga_visit_type (e.g. NAD)
   * @param {string} [couponCode] - Coupon code or affiliate slug for discount at order creation
   * @param {string} [referralCampaign] - Referral campaign (affiliate slug) for attribution
   * @returns {Promise<Object>} - {checkout_url, checkout_token, order_id, visit_id}
   */
  async createOrder(
    patientId,
    questionnaireAnswers,
    selectedMedicines,
    orderType = "onboarding",
    questionnaireSessionId = null,
    couponCode = null,
    referralCampaign = null,
    followupSessionId = null
  ) {
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    if (!clientApiEndpoint) {
      throw new Error(
        "Client API endpoint not found. Please refresh the page."
      );
    }

    const payload = {
      patient_id: patientId,
      questionnaire_answers: questionnaireAnswers,
      selected_medicines: selectedMedicines,
      order_type: orderType,
    };
    if (questionnaireSessionId) {
      payload.questionnaire_session_id = questionnaireSessionId;
    }
    if (couponCode && String(couponCode).trim()) {
      payload.coupon_code = String(couponCode).trim();
    }
    if (referralCampaign && String(referralCampaign).trim()) {
      payload.referral_campaign = String(referralCampaign).trim();
    }
    const attribution = getStoredAttribution();
    if (attribution) {
      payload.attribution = attribution;
    }
    const resolvedFollowupSessionId =
      followupSessionId ||
      sessionStorage.getItem("followup_session_id") ||
      sessionStorage.getItem("existing_patient_session_id");
    if (resolvedFollowupSessionId) {
      payload.followup_session_id = resolvedFollowupSessionId;
    }

    const headers = {
      "Content-Type": "application/json",
    };
    if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;
    const followUpAccessToken = sessionStorage.getItem("followup_access_token");
    if (followUpAccessToken) {
      headers["X-FollowUp-Access-Token"] = followUpAccessToken;
    }

    const clientId = this.getClientId();
    if (clientId) headers["X-Client-ID"] = clientId;

    console.log("🚀 Creating order for patient:", patientId);
    if (payload.coupon_code || payload.referral_campaign) {
      console.log("🎟️ Order create payload includes coupon/referral:", { coupon_code: payload.coupon_code, referral_campaign: payload.referral_campaign });
    }

    try {
      const response = await axios.post(
        joinApiEndpoint(clientApiEndpoint, API_PATH.orderCreate),
        payload,
        { headers, timeout: 60000 }
      );

      if (
        response.data &&
        (response.data.checkout_url || response.data.success)
      ) {
        console.log("✅ Order created successfully");
        return response.data;
      }

      throw new Error(response.data.error || "Failed to create order");
    } catch (error) {
      console.error("❌ Order creation failed:", error);
      throw new Error(getApiErrorMessage(error) || "Failed to create order. Please try again.");
    }
  }

  /**
   * Get checkout details for a checkout token
   * @param {string} checkoutToken - Checkout token from order creation
   * @returns {Promise<Object>} - Order details for checkout page
   */
  async getCheckoutDetails(checkoutToken) {
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    if (!clientApiEndpoint) {
      throw new Error(
        "Client API endpoint not found. Please refresh the page."
      );
    }

    const headers = {
      "Content-Type": "application/json",
    };
    if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;

    const clientId = this.getClientId();
    if (clientId) headers["X-Client-ID"] = clientId;

    console.log("🚀 Getting checkout details for token:", checkoutToken);

    try {
      const response = await axios.get(
        joinApiEndpoint(clientApiEndpoint, API_PATH.checkout(checkoutToken)),
        { headers, timeout: 30000 }
      );

      if (response.data && (response.data.order_id || response.data.order || response.data.success)) {
        console.log("✅ Checkout details retrieved successfully");
        return response.data;
      }

      throw new Error(response.data.error || "Failed to get checkout details");
    } catch (error) {
      console.error("❌ Failed to get checkout details:", error);
      if (error.response?.status === 404) {
        throw new Error(
          "Checkout link not found or expired. Please restart your order."
        );
      }
      throw new Error(getApiErrorMessage(error) || "Failed to load checkout details. Please try again.");
    }
  }

  /**
   * Submit payment for checkout (token-based).
   * @param {string} checkoutToken - Checkout token from URL
   * @param {Object} body - { payment_token?, payment_method_id?, coupon_code?, postal_code?, opaque_data_descriptor?, opaque_data_value?, ... }
   * @returns {Promise<{ success: boolean, redirect_url?: string }>}
   */
  async checkoutPay(checkoutToken, body = {}) {
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    if (!clientApiEndpoint) {
      throw new Error("Client API endpoint not found. Please refresh the page.");
    }

    const headers = { "Content-Type": "application/json" };
    if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;
    const clientId = this.getClientId();
    if (clientId) headers["X-Client-ID"] = clientId;

    const response = await axios.post(
      joinApiEndpoint(clientApiEndpoint, API_PATH.checkoutPay(checkoutToken)),
      body,
      { headers, timeout: 60000 }
    );

    if (response.data && response.data.success) {
      return response.data;
    }
    throw new Error(response.data?.error || "Payment failed.");
  }

  /**
   * Submit one release-bound aggregate treatment checkout. The payload contains
   * only the opaque capability and payment token; display Products/prices are
   * never posted as authority.
   */
  async submitCombinedCheckout({
    checkoutCapability,
    runtimeSessionRevision,
    idempotencyKey,
    paymentMethodToken,
    paymentMethod,
    labCheckout,
  }) {
    const clientApiEndpoint = localStorage.getItem("welliemd_client_api_endpoint");
    const apiToken = process.env.REACT_APP_API_TOKEN || "";
    if (!clientApiEndpoint) {
      throw new Error("Client API endpoint not found. Please refresh the page.");
    }
    const headers = { "Content-Type": "application/json" };
    if (apiToken) headers.Authorization = `Bearer ${apiToken}`;
    const clientId = this.getClientId();
    if (clientId) headers["X-Client-ID"] = clientId;
    try {
      const response = await axios.post(
        joinApiEndpoint(clientApiEndpoint, API_PATH.combinedCheckout),
        {
          checkout_capability: checkoutCapability,
          runtime_session_revision: runtimeSessionRevision,
          idempotency_key: idempotencyKey,
          payment_method_token: paymentMethodToken,
          payment_method: paymentMethod,
          lab_checkout: labCheckout || undefined,
        },
        { headers, timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      const normalized = new Error(getApiErrorMessage(error) || "Checkout could not be completed.");
      normalized.code = error?.response?.data?.code;
      normalized.status = error?.response?.status;
      normalized.retryable = Boolean(error?.response?.data?.retryable);
      normalized.checkoutRecovery = error?.response?.data?.checkout_recovery || null;
      normalized.traceId = error?.response?.data?.trace_id || null;
      normalized.details = error?.response?.data?.details || null;
      throw normalized;
    }
  }

  async getCombinedCheckoutPaymentMethods({
    checkoutCapability,
    runtimeSessionRevision,
  }) {
    const clientApiEndpoint = localStorage.getItem("welliemd_client_api_endpoint");
    const apiToken = process.env.REACT_APP_API_TOKEN || "";
    if (!clientApiEndpoint) {
      throw new Error("Client API endpoint not found. Please refresh the page.");
    }
    const headers = { "Content-Type": "application/json" };
    if (apiToken) headers.Authorization = `Bearer ${apiToken}`;
    const clientId = this.getClientId();
    if (clientId) headers["X-Client-ID"] = clientId;
    try {
      const response = await axios.post(
        joinApiEndpoint(clientApiEndpoint, API_PATH.combinedCheckoutPaymentMethods),
        {
          checkout_capability: checkoutCapability,
          runtime_session_revision: runtimeSessionRevision,
        },
        { headers, timeout: 30000 }
      );
      return response.data;
    } catch (error) {
      const normalized = new Error(
        getApiErrorMessage(error) || "Saved payment methods could not be loaded."
      );
      normalized.code = error?.response?.data?.code;
      normalized.status = error?.response?.status;
      normalized.details = error?.response?.data?.details || null;
      throw normalized;
    }
  }

  async getCombinedCheckoutRecovery(token) {
    const clientApiEndpoint = localStorage.getItem("welliemd_client_api_endpoint");
    const apiToken = process.env.REACT_APP_API_TOKEN || "";
    if (!clientApiEndpoint) {
      throw new Error("Client API endpoint not found. Please refresh the page.");
    }
    const headers = { "Content-Type": "application/json" };
    if (apiToken) headers.Authorization = `Bearer ${apiToken}`;
    const clientId = this.getClientId();
    if (clientId) headers["X-Client-ID"] = clientId;
    try {
      const response = await axios.get(
        joinApiEndpoint(clientApiEndpoint, API_PATH.combinedCheckoutRecovery(token)),
        { headers, timeout: 30000 },
      );
      return response.data;
    } catch (error) {
      const normalized = new Error(
        getApiErrorMessage(error) || "This checkout link is unavailable.",
      );
      normalized.code = error?.response?.data?.code;
      normalized.status = error?.response?.status;
      throw normalized;
    }
  }

  async removeCombinedCheckoutPaymentMethod({
    checkoutCapability,
    runtimeSessionRevision,
    paymentMethodId,
  }) {
    return this.mutateCombinedCheckoutPaymentMethod({
      operation: "remove",
      checkoutCapability,
      runtimeSessionRevision,
      paymentMethodId,
    });
  }

  async replaceCombinedCheckoutPaymentMethod({
    checkoutCapability,
    runtimeSessionRevision,
    paymentMethodId,
    replace_payment_method_id,
    gateway,
    ...paymentData
  }) {
    return this.mutateCombinedCheckoutPaymentMethod({
      operation: "replace",
      checkoutCapability,
      runtimeSessionRevision,
      paymentMethodId,
      replace_payment_method_id: replace_payment_method_id || paymentMethodId,
      gateway,
      ...paymentData,
    });
  }

  async mutateCombinedCheckoutPaymentMethod(payload) {
    const clientApiEndpoint = localStorage.getItem("welliemd_client_api_endpoint");
    const apiToken = process.env.REACT_APP_API_TOKEN || "";
    if (!clientApiEndpoint) throw new Error("Client API endpoint not found. Please refresh the page.");
    const headers = { "Content-Type": "application/json" };
    if (apiToken) headers.Authorization = `Bearer ${apiToken}`;
    const clientId = this.getClientId();
    if (clientId) headers["X-Client-ID"] = clientId;
    const requestPayload = {
      ...payload,
      checkout_capability: payload.checkoutCapability,
      runtime_session_revision: payload.runtimeSessionRevision,
      payment_method_id: payload.paymentMethodId,
    };
    delete requestPayload.checkoutCapability;
    delete requestPayload.runtimeSessionRevision;
    delete requestPayload.paymentMethodId;
    try {
      const response = await axios.post(
        joinApiEndpoint(clientApiEndpoint, API_PATH.combinedCheckoutPaymentMethods),
        requestPayload,
        { headers, timeout: 60000 },
      );
      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error) || "Payment method update failed.");
    }
  }

  async recoverCombinedCheckout({
    checkoutCapability,
    runtimeSessionRevision,
    idempotencyKey,
    recoveryAction,
  }) {
    const clientApiEndpoint = localStorage.getItem("welliemd_client_api_endpoint");
    const apiToken = process.env.REACT_APP_API_TOKEN || "";
    if (!clientApiEndpoint) {
      throw new Error("Client API endpoint not found. Please refresh the page.");
    }
    const headers = { "Content-Type": "application/json" };
    if (apiToken) headers.Authorization = `Bearer ${apiToken}`;
    const clientId = this.getClientId();
    if (clientId) headers["X-Client-ID"] = clientId;
    try {
      const response = await axios.post(
        joinApiEndpoint(clientApiEndpoint, API_PATH.combinedCheckout),
        {
          operation: "recover",
          checkout_capability: checkoutCapability,
          runtime_session_revision: runtimeSessionRevision,
          idempotency_key: idempotencyKey,
          recovery_action: recoveryAction,
        },
        { headers, timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      const normalized = new Error(
        getApiErrorMessage(error) || "Checkout recovery could not be completed."
      );
      normalized.code = error?.response?.data?.code;
      normalized.status = error?.response?.status;
      normalized.details = error?.response?.data?.details || null;
      throw normalized;
    }
  }

  /**
   * Remove a saved payment method while on token-based checkout.
   * The checkout token scopes deletion to the order's patient.
   */
  async deleteCheckoutPaymentMethod(checkoutToken, paymentMethodId) {
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    if (!clientApiEndpoint) {
      throw new Error("Client API endpoint not found. Please refresh the page.");
    }

    const headers = { "Content-Type": "application/json" };
    if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;
    const clientId = this.getClientId();
    if (clientId) headers["X-Client-ID"] = clientId;

    try {
      await axios.delete(
        joinApiEndpoint(clientApiEndpoint, API_PATH.checkoutPaymentMethod(checkoutToken, paymentMethodId)),
        { headers, timeout: 30000 }
      );
    } catch (error) {
      throw new Error(getApiErrorMessage(error) || "Failed to remove payment method.");
    }
  }

  /**
   * Save a payment method while on token-based checkout.
   * Used by checkout card edit flow before Pay Now can use the replacement card.
   */
  async saveCheckoutPaymentMethod(checkoutToken, body = {}) {
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    if (!clientApiEndpoint) {
      throw new Error("Client API endpoint not found. Please refresh the page.");
    }

    const headers = { "Content-Type": "application/json" };
    if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;
    const clientId = this.getClientId();
    if (clientId) headers["X-Client-ID"] = clientId;

    try {
      const response = await axios.post(
        joinApiEndpoint(clientApiEndpoint, API_PATH.checkoutPaymentMethods(checkoutToken)),
        body,
        { headers, timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error) || "Failed to save payment method.");
    }
  }

  /**
   * Refresh an expired checkout token
   * @param {string} expiredToken - The expired checkout token
   * @returns {Promise<Object>} - New checkout details with fresh token
   */
  async refreshCheckoutToken(expiredToken) {
    const clientApiEndpoint = localStorage.getItem(
      "welliemd_client_api_endpoint"
    );
    const apiToken = process.env.REACT_APP_API_TOKEN || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MjAyNS0wOS0xMFQxNzozMzo1NS4yNzBa.Y1mPg3KQx9Z2uzZ-4O7zIhR6iT3uK8m4Q7pWzAqJr8U";

    if (!clientApiEndpoint) {
      throw new Error(
        "Client API endpoint not found. Please refresh the page."
      );
    }

    const headers = {
      "Content-Type": "application/json",
    };
    if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;

    const clientId = this.getClientId();
    if (clientId) headers["X-Client-ID"] = clientId;

    console.log("🚀 Refreshing checkout token:", expiredToken);

    try {
      const response = await axios.post(
        joinApiEndpoint(clientApiEndpoint, API_PATH.checkoutRefreshToken),
        { expired_token: expiredToken },
        { headers, timeout: 30000 }
      );

      if (
        response.data &&
        (response.data.checkout_url || response.data.success)
      ) {
        console.log("✅ Checkout token refreshed successfully");
        return response.data;
      }

      throw new Error(
        response.data.error || "Failed to refresh checkout token"
      );
    } catch (error) {
      console.error("❌ Failed to refresh checkout token:", error);
      throw new Error(getApiErrorMessage(error) || "Failed to refresh checkout token. Please restart your order.");
    }
  }

  async submitResponse(questionId, responseData) {
    if (this.isFileUpload(responseData)) {
      return await this.submitFileResponse(questionId, responseData);
    }

    const response = await this.api.post(
      "/questionnaires/sessions/process-response/",
      {
        question_id: questionId,
        response_data: responseData,
        response_time_ms: Date.now() - (this.responseStartTime || 0),
      }
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || "Failed to submit response");
  }

  async submitFileResponse(questionId, fileData) {
    const base64Data = await this.fileToBase64(fileData);

    const response = await this.api.post(
      "/questionnaires/sessions/process-response/",
      {
        question_id: questionId,
        response_data: {
          file_data: base64Data,
          file_name: fileData.name,
          file_size: fileData.size,
          mime_type: fileData.type,
        },
        response_time_ms: Date.now() - (this.responseStartTime || 0),
      }
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || "Failed to submit file response");
  }

  async completeQuestionnaire(data = {}) {
    const payload = { patient_info: data.patient_info || {} };

    const response = await this.api.post(
      "/questionnaires/sessions/complete-questionnaire/",
      payload
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(
      response.data.message || "Failed to complete questionnaire"
    );
  }

  isFileUpload(data) {
    return data instanceof File || (data && data.constructor === File);
  }

  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  markResponseStart() {
    this.responseStartTime = Date.now();
  }
}

export default new WellieMDAPI();
